import * as React from 'react';

import { FixedRatio, BreakpointFlag, Button, SchemalessResponsiveImage, Box } from '@packages/ui';
import { ResponsiveValue } from 'styled-system';
import type { CustomizableText, DyImages } from '../types';
import { StyledCTAWrapper, StyledInnerWrapper, StyledSubitle, StyledTitle } from './HeroBannerSingleCTAStyleComponents';
import { SmartLink } from '../../SmartLink';
import { useGetDyImageSources } from '../utils/useGetDyImageSources';

export enum HeroBannerSingleCTAKind {
  InGrid,
  OutsideGrid,
}

export interface HeroBannerSingleCTAData {
  title?: CustomizableText;
  subtitle?: CustomizableText;
  ctaButton: {
    backgroundColor: string;
    text: string;
    textColor: string;
    linkUrl: string;
  };
  images: DyImages;
  kind: HeroBannerSingleCTAKind;
}

export interface HeroBannerSingleCTAProps extends HeroBannerSingleCTAData {
  onClick: VoidFunction;
}

function getInnerPadding(kind: HeroBannerSingleCTAKind): ResponsiveValue<string | number> {
  switch (kind) {
    case HeroBannerSingleCTAKind.InGrid: {
      return 4;
    }

    case HeroBannerSingleCTAKind.OutsideGrid: {
      return [2, 2, 3, '150px'];
    }

    default:
      return [];
  }
}

export const HeroBannerSingleCTA: React.FC<HeroBannerSingleCTAProps> = ({
  title = {},
  subtitle = {},
  kind,
  images,
  ctaButton,
  onClick,
}) => {
  const { text: titleText, color: titleColor, size: titleSize = 13 } = title;
  const { text: subtitleText, color: subtitleColor, size: subtitleSize = 6 } = subtitle;
  const { desktop, tablet, mobile } = images;
  const { sources, cdnProvider } = useGetDyImageSources(images);
  const [mobileSrc, tabletSrc, desktopSrc] = sources;

  const mobileRatio = Number(mobile.height) / Number(mobile.width);
  const tabletRatio = Number(tablet.height) / Number(tablet.width);
  const desktopRatio = Number(desktop.height) / Number(desktop.width);

  const mx = kind === HeroBannerSingleCTAKind.InGrid ? [1, 1, 2] : [];

  return (
    // if we don't wrap this inside a block element with 100% width, it will
    // have no width at all when placed inside a Flexbox
    <Box width="100%">
      <FixedRatio ratio={[mobileRatio, mobileRatio, tabletRatio, desktopRatio]}>
        <StyledInnerWrapper px={getInnerPadding(kind)} mx={mx}>
          {title && (
            <StyledTitle fontSize={titleSize} color={titleColor}>
              {titleText}
            </StyledTitle>
          )}
          {subtitle && (
            <StyledSubitle fontSize={subtitleSize} color={subtitleColor}>
              {subtitleText}
            </StyledSubitle>
          )}
          <StyledCTAWrapper onClick={onClick}>
            <SmartLink linkUrl={ctaButton.linkUrl} target="_self">
              <Button background={{ background: ctaButton.backgroundColor }} color={ctaButton.textColor}>
                {ctaButton.text}
              </Button>
            </SmartLink>
          </StyledCTAWrapper>
        </StyledInnerWrapper>
        <SchemalessResponsiveImage
          cdnProvider={cdnProvider}
          mapping={{
            [BreakpointFlag.SM]: mobileSrc,
            [BreakpointFlag.MD]: tabletSrc,
            [BreakpointFlag.LG]: desktopSrc,
          }}
        />
      </FixedRatio>
    </Box>
  );
};
